import React, { useState, useEffect } from 'react';
import UserManagement from './UserManagement';
import RegimenesLaborales from './RegimenesLaborales';
import informacionGeneralService from '../services/informacionGeneralService';
import afpService from '../services/afpService';
import tramoRentaService from '../services/tramoRentaService';
import plameConfiguracionService from '../services/plameConfiguracionService';
import formulaService from '../services/formulaService';
import { ENV_CONFIG } from '../config/environment';

const CompanySelector = ({ user, onSelectCompany }) => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentView, setCurrentView] = useState('company-selector'); // 'company-selector' | 'user-management' | 'system-config'
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para el modal de crear empresa
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [formData, setFormData] = useState({
    ruc: '',
    nombre_empresa: '',
    descripcion: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Estados para mensajes
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });

  console.log('🏢 CompanySelector - Renderizado');
  console.log('👤 Usuario recibido:', user);
  console.log('🔄 onSelectCompany función:', typeof onSelectCompany);
  console.log('🔄 user ROLES:', user?.roles);
  console.log('🔄 user PERMISSIONS:', user?.permissions);
  
  // Logging adicional para debug
  console.log('🔐 Estado de autenticación:', {
    userExists: !!user,
    userEmail: user?.email,
    userToken: user?.token ? `${user.token.substring(0, 20)}...` : 'No token en user',
    localStorageToken: localStorage.getItem(ENV_CONFIG.SANCTUM_TOKEN_KEY) ? 'Existe' : 'No existe',
    localStorageUser: localStorage.getItem(ENV_CONFIG.USER_KEY) ? 'Existe' : 'No existe'
  });

  // Verificar si es Super Administrador o Admin
  const isSuperAdmin = user?.roles?.some(role => role.name === 'super_admin') || user?.email === 'admin@planillas.com';
  const isAdmin = user?.roles?.some(role => role.name === 'admin') || isSuperAdmin;
  console.log('🔑 Es Super Admin:', isSuperAdmin);
  console.log('🔑 Es Admin:', isAdmin);

  // Función auxiliar para verificar si tiene un rol específico
  const hasRole = (roleName) => {
    return user?.roles?.some(role => role.name === roleName);
  };

  // Función auxiliar para verificar si tiene un permiso específico
  const hasPermission = (permissionName) => {
    return user?.permissions?.some(permission => permission.name === permissionName);
  };

  console.log('🔍 Verificaciones de roles:');
  console.log('  - Super Admin:', hasRole('super_admin'));
  console.log('  - Admin:', hasRole('admin'));
  console.log('  - HR Manager:', hasRole('hr_manager'));
  console.log('🔍 Verificaciones de permisos:');
  console.log('  - users.create:', hasPermission('users.create'));
  console.log('  - users.read:', hasPermission('users.read'));

  // Función para mostrar mensajes
  const mostrarMensaje = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje({ tipo: '', texto: '' }), 5000);
  };

  // Función para obtener empresas desde la API
  const fetchCompanies = async () => {
    try {
      console.log('🔄 fetchCompanies - Iniciando...');
      setLoadingCompanies(true);
      setError(null);
      
      // Verificar localStorage completo
      console.log('📦 localStorage completo:', {
        planillas_token: localStorage.getItem(ENV_CONFIG.SANCTUM_TOKEN_KEY),
        planillas_user: localStorage.getItem(ENV_CONFIG.USER_KEY),
        allKeys: Object.keys(localStorage)
      });
      
      const token = localStorage.getItem(ENV_CONFIG.SANCTUM_TOKEN_KEY);
      console.log('🔑 Token obtenido:', token ? `${token.substring(0, 20)}...` : 'NULL');
      
      if (!token) {
        console.log('❌ No hay token en localStorage');
        throw new Error('No hay token de autenticación');
      }

      console.log('🌐 Haciendo petición a API...');
      console.log('📍 URL:', `${ENV_CONFIG.API_BASE_URL}/empresas`);
      console.log('🔧 Headers:', {
        'Authorization': `Bearer ${token.substring(0, 20)}...`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      });

      // Verificar si el servidor está disponible
      try {
        const healthCheck = await fetch(`${ENV_CONFIG.API_BASE_URL}/auth/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        console.log('🏥 Health check del servidor:', {
          status: healthCheck.status,
          ok: healthCheck.ok
        });
      } catch (healthError) {
        console.log('⚠️ Servidor no disponible:', healthError.message);
      }

      const response = await fetch(`${ENV_CONFIG.API_BASE_URL}/empresas`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log('📡 Respuesta recibida:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Error response body:', errorText);
        throw new Error(`Error ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      console.log('🏢 Datos completos de la API:', data);
      console.log('📊 Tipo de datos:', typeof data);
      console.log('🔍 Estructura de datos:', {
        hasStatus: 'status' in data,
        hasData: 'data' in data,
        hasMessage: 'message' in data,
        dataType: typeof data.data,
        dataLength: Array.isArray(data.data) ? data.data.length : 'No es array'
      });

      if (data.status === 'success') {
        console.log('✅ API respondió exitosamente');
        console.log('📋 Empresas encontradas:', data.data?.length || 0);
        console.log('🎨 Empresas con colores del backend:', data.data);
        setCompanies(data.data || []);
      } else {
        console.log('⚠️ API respondió con error:', data.message);
        throw new Error(data.message || 'Error al obtener empresas');
      }
    } catch (error) {
      console.error('❌ Error completo al obtener empresas:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        cause: error.cause
      });
      console.error('🔍 Detalles del error:', error);
      setError(error.message);
      // No mostrar empresas de ejemplo, dejar vacío para mostrar mensaje apropiado
      setCompanies([]);
    } finally {
      console.log('🏁 fetchCompanies - Finalizando...');
      setLoadingCompanies(false);
    }
  };



  // useEffect para cargar empresas al montar el componente
  useEffect(() => {
    fetchCompanies();
  }, []);



  const handleSelectCompany = (company) => {
    console.log('🎯 CompanySelector - Empresa seleccionada:', company);
    setSelectedCompany(company);
  };

  const handleContinue = () => {
    console.log('▶️ CompanySelector - handleContinue iniciado');
    console.log('🏢 Empresa seleccionada:', selectedCompany);
    
    if (!selectedCompany) {
      console.log('⚠️ CompanySelector - No hay empresa seleccionada');
      return;
    }
    
    setIsLoading(true);
    
    // Simular carga
    setTimeout(() => {
      console.log('✅ CompanySelector - Llamando onSelectCompany');
      onSelectCompany(selectedCompany);
      setIsLoading(false);
    }, 1500);
  };

  const handleLogout = () => {
    console.log('🚪 CompanySelector - handleLogout iniciado');
    if (window.confirm('¿Estás seguro de que deseas cerrar sesión?')) {
      // Limpiar todas las claves de autenticación
      localStorage.removeItem(ENV_CONFIG.SANCTUM_TOKEN_KEY);
      localStorage.removeItem(ENV_CONFIG.USER_KEY);
      localStorage.removeItem(ENV_CONFIG.COMPANY_KEY);
      window.location.reload();
    }
  };

  // Funciones para opciones de configuración del Super Admin
  const handleCreateCompany = () => {
    console.log('🏢 CompanySelector - Abrir modal crear empresa');
    setShowCreateModal(true);
    setCreateError(null);
    setFormErrors({});
    setFormData({
      ruc: '',
      nombre_empresa: '',
      descripcion: ''
    });
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setShowCreateModal(false);
    setCreateError(null);
    setFormErrors({});
    setFormData({
      ruc: '',
      nombre_empresa: '',
      descripcion: ''
    });
  };

  // Función para manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Función para validar el formulario
  const validateForm = () => {
    const errors = {};
    
    // Validar RUC
    if (!formData.ruc.trim()) {
      errors.ruc = 'El RUC es obligatorio';
    } else if (formData.ruc.length !== 11) {
      errors.ruc = 'El RUC debe tener exactamente 11 dígitos';
    } else if (!/^\d+$/.test(formData.ruc)) {
      errors.ruc = 'El RUC solo debe contener números';
    }
    
    // Validar nombre de empresa
    if (!formData.nombre_empresa.trim()) {
      errors.nombre_empresa = 'El nombre de la empresa es obligatorio';
    } else if (formData.nombre_empresa.length > 250) {
      errors.nombre_empresa = 'El nombre no puede exceder 250 caracteres';
    }
    
    // Validar descripción (opcional)
    if (formData.descripcion && formData.descripcion.length > 500) {
      errors.descripcion = 'La descripción no puede exceder 500 caracteres';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Función para crear la empresa
  const handleSubmitCreateCompany = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    console.log('📤 Creando empresa:', formData);
    setCreatingCompany(true);
    setCreateError(null);
    
    try {
      const token = localStorage.getItem(ENV_CONFIG.SANCTUM_TOKEN_KEY);
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await fetch(`${ENV_CONFIG.API_BASE_URL}/empresas`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      console.log('📥 Respuesta crear empresa:', data);

      if (!response.ok) {
        if (data.errors) {
          setFormErrors(data.errors);
        }
        throw new Error(data.message || `Error ${response.status}: ${response.statusText}`);
      }

      if (data.status === 'success') {
        console.log('✅ Empresa creada exitosamente:', data.data);
        
        // Agregar la nueva empresa a la lista (ya viene con colores del backend)
        setCompanies(prev => [...prev, data.data]);
        
        // Cerrar modal
        handleCloseModal();
        
        // Mostrar mensaje de éxito
        alert('¡Empresa creada exitosamente!');
      } else {
        throw new Error(data.message || 'Error al crear empresa');
      }
    } catch (error) {
      console.error('❌ Error al crear empresa:', error);
      setCreateError(error.message);
    } finally {
      setCreatingCompany(false);
    }
  };

  const handleManageUsers = () => {
    console.log('👥 CompanySelector - Gestionar usuarios');
    setCurrentView('user-management');
  };

  const handleSystemSettings = () => {
    console.log('⚙️ CompanySelector - Configuración del sistema');
    setCurrentView('system-config');
  };

  const handleBackToCompanySelector = () => {
    setCurrentView('company-selector');
  };

  // Renderizar la vista correspondiente
  if (currentView === 'user-management') {
    return <UserManagement user={user} onBack={handleBackToCompanySelector} />;
  }

  if (currentView === 'system-config') {
    return <SystemConfiguration user={user} onBack={handleBackToCompanySelector} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-20 w-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
            <span className="text-white text-3xl font-bold">P</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Seleccionar Empresa
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Hola {user?.name}, selecciona la empresa con la que deseas trabajar
          </p>
        </div>

        {/* Panel de Configuración Administrativa - Super Admin y Admin */}
        {isAdmin && (
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl shadow-lg p-6 border border-orange-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-sm">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {isSuperAdmin ? 'Panel de Super Administrador' : 'Panel de Administrador'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {isSuperAdmin ? 'Acceso completo al sistema' : 'Gestión administrativa del sistema'}
                  </p>
                </div>
              </div>
                              <div className="flex items-center space-x-2">
                  <div className={`px-3 py-1 text-xs font-medium rounded-full ${
                    isSuperAdmin 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {isSuperAdmin ? 'SUPER ADMIN' : 'ADMIN'}
                  </div>
                </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Crear Empresa */}
              <button
                onClick={handleCreateCompany}
                className="flex items-center space-x-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all duration-200 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <div className="text-left">
                  <h4 className="font-medium text-gray-900">Crear Empresa</h4>
                  <p className="text-sm text-gray-600">Nueva empresa</p>
                </div>
              </button>

              {/* Gestionar Usuarios */}
              <button
                onClick={handleManageUsers}
                className="flex items-center space-x-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all duration-200 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <h4 className="font-medium text-gray-900">Gestionar Usuarios</h4>
                  <p className="text-sm text-gray-600">Usuarios del sistema</p>
                </div>
              </button>

              {/* Configuración del Sistema */}
              <button
                onClick={handleSystemSettings}
                className="flex items-center space-x-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all duration-200 group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                  </svg>
                </div>
                <div className="text-left">
                  <h4 className="font-medium text-gray-900">Configuración</h4>
                  <p className="text-sm text-gray-600">Sistema global</p>
                </div>
              </button>
            </div>

          </div>
        )}

        {/* Company Grid */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-700 text-sm">
                  Error al cargar empresas: {error}
                </p>
              </div>
            </div>
          )}

          {/* No Companies Message */}
          {!loadingCompanies && !error && companies.length === 0 && (
            <div className="mb-6 p-8 bg-blue-50 border border-blue-200 rounded-lg text-center">
              <div className="flex flex-col items-center">
                <svg className="w-16 h-16 text-blue-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No hay empresas registradas
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Aún no se han creado empresas en el sistema. 
                  {isSuperAdmin && ' Como Super Administrador, puedes crear la primera empresa.'}
                </p>
                {isSuperAdmin && (
                  <button
                    onClick={handleCreateCompany}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span>Crear Primera Empresa</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Loading State */}
          {loadingCompanies ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {[1, 2, 3, 4].map((index) => (
                <div key={index} className="animate-pulse">
                  <div className="p-6 rounded-xl border-2 border-gray-200">
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-gray-300 rounded-xl"></div>
                      <div className="flex-1 min-w-0">
                        <div className="h-5 bg-gray-300 rounded mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
                        <div className="flex items-center space-x-4">
                          <div className="h-3 bg-gray-200 rounded w-20"></div>
                          <div className="h-3 bg-gray-200 rounded w-24"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : companies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {companies.map((company) => (
              <div
                key={company.id}
                onClick={() => handleSelectCompany(company)}
                className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  selectedCompany?.id === company.id
                    ? `${company.borderColor} ${company.bgColor} shadow-md`
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Selected indicator */}
                {selectedCompany?.id === company.id && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-4">
                  <div className={`w-16 h-16 bg-gradient-to-br ${company.color || 'from-teal-500 to-teal-600'} rounded-xl flex items-center justify-center shadow-sm`}>
                    <span className="text-white text-xl font-bold">
                      {company.logo || company.name?.substring(0, 2).toUpperCase() || 'EM'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {company.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      {company.description}
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        RUC: {company.ruc}
                      </span>
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                        {company.employees} empleados
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          ) : null}

          {/* Action Buttons */}
          {!loadingCompanies && (
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Cerrar Sesión</span>
            </button>

            <button
              onClick={handleContinue}
              disabled={!selectedCompany || isLoading}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all duration-200"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Accediendo...</span>
                </>
              ) : (
                <>
                  <span>Continuar</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </div>
          )}

          {/* Company info display */}
          {!loadingCompanies && selectedCompany && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Empresa seleccionada:</strong> {selectedCompany.name}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Accederás al sistema de planillas de esta empresa
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal para Crear Empresa */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header del Modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Crear Nueva Empresa</h3>
                  <p className="text-sm text-gray-600">Registra una nueva empresa en el sistema</p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={creatingCompany}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenido del Modal */}
            <form onSubmit={handleSubmitCreateCompany} className="p-6">
              {/* Error General */}
              {createError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-700 text-sm">{createError}</p>
                  </div>
                </div>
              )}

              {/* Campo RUC */}
              <div className="mb-4">
                <label htmlFor="ruc" className="block text-sm font-medium text-gray-700 mb-2">
                  RUC <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="ruc"
                  name="ruc"
                  value={formData.ruc}
                  onChange={handleInputChange}
                  maxLength="11"
                  placeholder="20123456789"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    formErrors.ruc ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={creatingCompany}
                />
                {formErrors.ruc && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.ruc}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">Ingresa el RUC de 11 dígitos de la empresa</p>
              </div>

              {/* Campo Nombre de Empresa */}
              <div className="mb-4">
                <label htmlFor="nombre_empresa" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de la Empresa <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="nombre_empresa"
                  name="nombre_empresa"
                  value={formData.nombre_empresa}
                  onChange={handleInputChange}
                  maxLength="250"
                  placeholder="Mi Empresa S.A.C."
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    formErrors.nombre_empresa ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={creatingCompany}
                />
                {formErrors.nombre_empresa && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.nombre_empresa}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.nombre_empresa.length}/250 caracteres
                </p>
              </div>

              {/* Campo Descripción */}
              <div className="mb-6">
                <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción <span className="text-gray-400">(Opcional)</span>
                </label>
                <textarea
                  id="descripcion"
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  maxLength="500"
                  rows="3"
                  placeholder="Descripción de la empresa y sus actividades..."
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none ${
                    formErrors.descripcion ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={creatingCompany}
                />
                {formErrors.descripcion && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.descripcion}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.descripcion.length}/500 caracteres
                </p>
              </div>

              {/* Botones */}
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                  disabled={creatingCompany}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creatingCompany}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {creatingCompany ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Creando...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>Crear Empresa</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      

      {/* Mensajes */}
      {mensaje.texto && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`p-4 rounded-lg shadow-lg ${
            mensaje.tipo === 'success' 
              ? 'bg-green-100 border border-green-400 text-green-700' 
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            <div className="flex items-center">
              {mensaje.tipo === 'success' ? (
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {mensaje.texto}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente de Configuración del Sistema
const SystemConfiguration = ({ user, onBack }) => {
  const [activeTab, setActiveTab] = useState('sueldo-minimo');
  const [loading, setLoading] = useState(false);

  // Estados para Sueldo Mínimo
  const [sueldoMinimo, setSueldoMinimo] = useState([]);

  // Estados para UIT
  const [uitData, setUitData] = useState([]);

  // Estados para AFP
  const [afpData, setAfpData] = useState([]);
  const [loadingAfp, setLoadingAfp] = useState(false);
  const [afpError, setAfpError] = useState(null);
  
  // Estados para modales de AFP
  const [showAfpModal, setShowAfpModal] = useState(false);
  const [afpModalMode, setAfpModalMode] = useState('create'); // 'create' | 'edit'
  const [currentAfp, setCurrentAfp] = useState(null);
  const [afpFormData, setAfpFormData] = useState({
    nombre: '',
    comision_fija: '',
    comision_variable: '',
    seguro: '',
    aporte: ''
  });
  const [afpFormErrors, setAfpFormErrors] = useState({});
  const [savingAfp, setSavingAfp] = useState(false);

  // Estados para Tramos de Renta
  const [tramosRentaData, setTramosRentaData] = useState([]);
  const [loadingTramosRenta, setLoadingTramosRenta] = useState(false);
  const [tramosRentaError, setTramosRentaError] = useState(null);
  
  // Estados para modales de Tramos de Renta
  const [showTramoRentaModal, setShowTramoRentaModal] = useState(false);
  const [tramoRentaModalMode, setTramoRentaModalMode] = useState('create'); // 'create' | 'edit'
  const [currentTramoRenta, setCurrentTramoRenta] = useState(null);
  const [tramoRentaFormData, setTramoRentaFormData] = useState({
    tramo: '',
    desde: '',
    hasta: '',
    tasa: ''
  });
  const [tramoRentaFormErrors, setTramoRentaFormErrors] = useState({});
  const [savingTramoRenta, setSavingTramoRenta] = useState(false);

  // Estados para PLAME
  const [plameData, setPlameData] = useState([]);
  const [loadingPlame, setLoadingPlame] = useState(false);
  const [plameError, setPlameError] = useState(null);
  
  // Estados para filtros PLAME
  const [filtroCodigo, setFiltroCodigo] = useState('');
  const [filtroNombre, setFiltroNombre] = useState('');
  // Modal edición código PLAME
  const [plameEditRow, setPlameEditRow] = useState(null);
  const [savingPlameEdit, setSavingPlameEdit] = useState(false);
  const [plameEditError, setPlameEditError] = useState(null);

  // Estados para Fórmulas
  const [formulasData, setFormulasData] = useState([]);
  const [loadingFormulas, setLoadingFormulas] = useState(false);
  const [formulasError, setFormulasError] = useState(null);
  
  // Estados auxiliares para fórmulas
  const [codigosPlameFormulas, setCodigosPlameFormulas] = useState([]);
  const [regimenesLaboralesFormulas, setRegimenesLaboralesFormulas] = useState([]);

  // Estados para modales de fórmulas
  const [showFormulaModal, setShowFormulaModal] = useState(false);
  const [formulaModalMode, setFormulaModalMode] = useState('create'); // 'create' o 'edit'
  const [currentFormula, setCurrentFormula] = useState(null);
  const [formulaFormData, setFormulaFormData] = useState({
    codigo_plame: '',
    regimen_laboral_id: '',
    formula: '',
    searchPlame: '',
    showPlameList: false
  });
  const [formulaFormErrors, setFormulaFormErrors] = useState({});

  // Verificar si es administrador
  const isSuperAdmin = user?.roles?.some(role => role.name === 'super_admin') || user?.email === 'admin@planillas.com';
  const isAdmin = user?.roles?.some(role => role.name === 'admin') || isSuperAdmin;

  // Estados para modales de edición
  const [editModal, setEditModal] = useState({ open: false, type: '', currentValue: 0 });
  const [newValue, setNewValue] = useState('');
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });

  // Función para cargar Tramos de Renta desde la API
  const fetchTramosRentaData = async () => {
    try {
      console.log('🔄 Cargando datos de Tramos de Renta...');
      setLoadingTramosRenta(true);
      setTramosRentaError(null);
      
      const response = await tramoRentaService.obtenerTramosRenta();
      console.log('✅ Tramos de Renta cargados:', response);
      
      if (response.success) {
        setTramosRentaData(response.data || []);
      } else {
        throw new Error(response.message || 'Error al cargar Tramos de Renta');
      }
    } catch (error) {
      console.error('❌ Error al cargar Tramos de Renta:', error);
      setTramosRentaError(error.message);
      setTramosRentaData([]);
    } finally {
      setLoadingTramosRenta(false);
    }
  };

  // Función para cargar PLAME desde la API
  const fetchPlameData = async () => {
    try {
      console.log('🔄 Cargando datos de PLAME...');
      setLoadingPlame(true);
      setPlameError(null);
      
      // Cargar todas las configuraciones sin paginación
      const response = await plameConfiguracionService.obtenerConfiguraciones({ per_page: 1000 });
      console.log('✅ Configuraciones PLAME cargadas:', response);
      
      if (response.success) {
        // Transformar datos de la API al formato esperado por la tabla
        const transformedData = response.data.data.map(config => ({
          id: config.id,
          codigo: config.codigo_plame,
          descripcion: config.descripcion,
          empleador: {
            essalud_regular: config.essalud_regular ? 'SI' : 'NO',
            essalud_cbssp_seg: config.essalud_cbssp_seg ? 'SI' : 'NO',
            essalud_agrario: config.essalud_agrario ? 'SI' : 'NO',
            essalud_sctr: config.essalud_sctr ? 'SI' : 'NO',
            impuesto_extraord_solidaridad: config.impuesto_extraord_solid ? 'SI' : 'NO',
            fondo_derechos_artista: config.fondo_derechos_artista ? 'SI' : 'NO',
            senati: config.senati ? 'SI' : 'NO'
          },
          trabajador: {
            sistema_nacional_19990: config.sistema_nacional_19990 ? 'SI' : 'NO',
            sistema_privado_pensiones: config.sistema_privado_pension ? 'SI' : 'NO',
            fondo_compl_jubil: config.fondo_compl_jubil ? 'SI' : 'NO',
            reg_esp_pensiones_pesquero: config.reg_esp_pensiones_pesq ? 'SI' : 'NO',
            renta_5ta_categoria: config.renta_5ta_categoria ? 'SI' : 'NO'
          },
          pensionista: {
            essalud_regular_pensionista: config.essalud_regular_pension ? 'SI' : 'NO',
            contrib_solidaria_asistencia: config.contrib_solidaria_asist ? 'SI' : 'NO'
          },
          vigente: true
        }));
        
        setPlameData(transformedData);
      } else {
        throw new Error(response.message || 'Error al cargar configuraciones PLAME');
      }
    } catch (error) {
      console.error('❌ Error al cargar PLAME:', error);
      setPlameError(error.message);
      setPlameData([]);
    } finally {
      setLoadingPlame(false);
    }
  };

  // Actualizar un campo en el formulario de edición PLAME
  const updatePlameEditField = (segment, field, value) => {
    setPlameEditRow(prev => {
      if (!prev) return prev;
      if (segment === 'root') return { ...prev, [field]: value };
      return { ...prev, [segment]: { ...prev[segment], [field]: value } };
    });
  };

  // Guardar edición de un código PLAME (modal) — payload con nombres esperados por el backend
  const savePlameEdit = async (edited) => {
    if (!edited?.id) return;
    setPlameEditError(null);
    setSavingPlameEdit(true);
    try {
      const emp = edited.empleador || {};
      const trab = edited.trabajador || {};
      const pen = edited.pensionista || {};
      const payload = {
        descripcion: edited.descripcion,
        essalud_regular: emp.essalud_regular === 'SI',
        essalud_cbssp_seg: emp.essalud_cbssp_seg === 'SI',
        essalud_agrario: emp.essalud_agrario === 'SI',
        essalud_sctr: emp.essalud_sctr === 'SI',
        impuesto_extraord_solid: emp.impuesto_extraord_solidaridad === 'SI',
        fondo_derechos_artista: emp.fondo_derechos_artista === 'SI',
        senati: emp.senati === 'SI',
        sistema_nacional_19990: trab.sistema_nacional_19990 === 'SI',
        sistema_privado_pension: trab.sistema_privado_pensiones === 'SI',
        fondo_compl_jubil: trab.fondo_compl_jubil === 'SI',
        reg_esp_pensiones_pesq: trab.reg_esp_pensiones_pesquero === 'SI',
        renta_5ta_categoria: trab.renta_5ta_categoria === 'SI',
        essalud_regular_pension: pen.essalud_regular_pensionista === 'SI',
        contrib_solidaria_asist: pen.contrib_solidaria_asistencia === 'SI'
      };
      const response = await plameConfiguracionService.actualizarConfiguracion(edited.id, payload);
      if (response && response.success) {
        setPlameEditRow(null);
        await fetchPlameData();
      } else {
        setPlameEditError((response && response.message) || 'Error al guardar');
      }
    } catch (err) {
      setPlameEditError(err.response?.data?.message || err.message || 'Error al guardar');
    } finally {
      setSavingPlameEdit(false);
    }
  };

  // Función para cargar Fórmulas desde la API
  const fetchFormulasData = async () => {
    try {
      console.log('🔄 Cargando datos de Fórmulas...');
      setLoadingFormulas(true);
      setFormulasError(null);
      
      const response = await formulaService.obtenerFormulas();
      console.log('✅ Fórmulas cargadas:', response);
      console.log('🔍 Tipo de response:', typeof response);
      console.log('🔍 Response.success:', response.success);
      console.log('🔍 Response.data:', response.data);
      console.log('🔍 Es array response.data?:', Array.isArray(response.data));
      
      if (response.success && response.data && response.data.data) {
        console.log('✅ Estableciendo formulasData con:', response.data.data);
        setFormulasData(response.data.data);
      } else {
        console.log('⚠️ No hay datos o success=false');
        console.log('🔍 response.data:', response.data);
        setFormulasData([]);
      }
    } catch (error) {
      console.error('❌ Error al cargar Fórmulas:', error);
      setFormulasError(error.message);
      setFormulasData([]);
    } finally {
      setLoadingFormulas(false);
    }
  };

  // Función para cargar códigos PLAME para fórmulas
  const fetchCodigosPlameFormulas = async () => {
    try {
      console.log('🔄 Cargando códigos PLAME para fórmulas...');
      const response = await formulaService.obtenerCodigosPlame();
      console.log('✅ Respuesta códigos PLAME:', response);
      
      if (response.success) {
        // Verificar si la respuesta tiene la estructura esperada
        const data = response.data?.data || response.data || [];
        console.log('📋 Códigos PLAME procesados:', data);
        setCodigosPlameFormulas(Array.isArray(data) ? data : []);
      } else {
        console.log('⚠️ Error en respuesta códigos PLAME:', response);
        setCodigosPlameFormulas([]);
      }
    } catch (error) {
      console.error('❌ Error al cargar códigos PLAME para fórmulas:', error);
      setCodigosPlameFormulas([]);
    }
  };

  // Función para cargar regímenes laborales para fórmulas
  const fetchRegimenesLaboralesFormulas = async () => {
    try {
      console.log('🔄 Cargando regímenes laborales para fórmulas...');
      const response = await formulaService.obtenerRegimenesLaborales();
      console.log('✅ Respuesta regímenes laborales:', response);
      
      if (response.success) {
        // Verificar si la respuesta tiene la estructura esperada
        const data = response.data?.data || response.data || [];
        console.log('👥 Regímenes laborales procesados:', data);
        setRegimenesLaboralesFormulas(Array.isArray(data) ? data : []);
      } else {
        console.log('⚠️ Error en respuesta regímenes laborales:', response);
        setRegimenesLaboralesFormulas([]);
      }
    } catch (error) {
      console.error('❌ Error al cargar regímenes laborales para fórmulas:', error);
      setRegimenesLaboralesFormulas([]);
    }
  };

  // Cargar información general al inicializar
  useEffect(() => {
    cargarInformacionGeneral();
  }, []);

  // Cargar AFP cuando cambie a la pestaña AFP
  useEffect(() => {
    if (activeTab === 'afp' && afpData.length === 0 && !loadingAfp) {
      fetchAfpData();
    }
  }, [activeTab, afpData.length, loadingAfp]);

  // Cargar Tramos de Renta cuando cambie a esa pestaña
  useEffect(() => {
    if (activeTab === 'tramos-renta' && tramosRentaData.length === 0 && !loadingTramosRenta) {
      fetchTramosRentaData();
    }
  }, [activeTab, tramosRentaData.length, loadingTramosRenta]);

  // Cargar PLAME cuando cambie a esa pestaña
  useEffect(() => {
    if (activeTab === 'plame' && plameData.length === 0 && !loadingPlame) {
      fetchPlameData();
    }
  }, [activeTab, plameData.length, loadingPlame]);

  // Cargar Fórmulas cuando cambie a esa pestaña
  useEffect(() => {
    if (activeTab === 'formulas' && formulasData.length === 0 && !loadingFormulas) {
      fetchFormulasData();
      // También cargar los datos auxiliares si no están cargados
      if (codigosPlameFormulas.length === 0) {
        fetchCodigosPlameFormulas();
      }
      if (regimenesLaboralesFormulas.length === 0) {
        fetchRegimenesLaboralesFormulas();
      }
    }
  }, [activeTab, formulasData.length, loadingFormulas, codigosPlameFormulas.length, regimenesLaboralesFormulas.length]);

  const cargarInformacionGeneral = async () => {
    try {
      setLoading(true);
      const response = await informacionGeneralService.obtenerInformacionActual();
      if (response.success) {
        // Convertir a formato esperado por la tabla
        if (response.data.sueldo_minimo) {
          setSueldoMinimo([{
            id: response.data.sueldo_minimo.id,
            monto: parseFloat(response.data.sueldo_minimo.monto),
            vigente: true
          }]);
        }
        if (response.data.uit) {
          setUitData([{
            id: response.data.uit.id,
            monto: parseFloat(response.data.uit.monto),
            vigente: true
          }]);
        }
      }
    } catch (error) {
      console.error('Error al cargar información general:', error);
      mostrarMensaje('error', 'Error al cargar la información general');
    } finally {
      setLoading(false);
    }
  };

  const mostrarMensaje = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje({ tipo: '', texto: '' }), 5000);
  };

  const abrirModalEdicion = (tipo, valorActual) => {
    setEditModal({ open: true, type: tipo, currentValue: valorActual });
    setNewValue(valorActual.toString());
  };

  const cerrarModalEdicion = () => {
    setEditModal({ open: false, type: '', currentValue: 0 });
    setNewValue('');
  };

  const guardarCambio = async () => {
    try {
      const monto = parseFloat(newValue);
      if (isNaN(monto) || monto <= 0) {
        mostrarMensaje('error', 'Por favor ingrese un monto válido');
        return;
      }

      let response;
      if (editModal.type === 'sueldo_minimo') {
        response = await informacionGeneralService.actualizarSueldoMinimo(monto);
      } else if (editModal.type === 'uit') {
        response = await informacionGeneralService.actualizarUit(monto);
      }

      if (response.success) {
        mostrarMensaje('success', response.message);
        cerrarModalEdicion();
        await cargarInformacionGeneral(); // Recargar datos
      }
    } catch (error) {
      const mensaje = error.response?.data?.message || 'Error al guardar los cambios';
      mostrarMensaje('error', mensaje);
    }
  };

  // Función para cargar AFP desde la API
  const fetchAfpData = async () => {
    try {
      console.log('🔄 Cargando datos de AFP...');
      setLoadingAfp(true);
      setAfpError(null);
      
      const response = await afpService.obtenerAfps();
      console.log('✅ AFP cargadas:', response);
      
      if (response.success) {
        setAfpData(response.data || []);
      } else {
        throw new Error(response.message || 'Error al cargar AFP');
      }
    } catch (error) {
      console.error('❌ Error al cargar AFP:', error);
      setAfpError(error.message);
      setAfpData([]);
    } finally {
      setLoadingAfp(false);
    }
  };

  // Funciones para manejar AFP
  const handleCreateAfp = () => {
    setAfpModalMode('create');
    setCurrentAfp(null);
    setAfpFormData({
      nombre: '',
      comision_fija: '',
      comision_variable: '',
      seguro: '',
      aporte: ''
    });
    setAfpFormErrors({});
    setShowAfpModal(true);
  };

  const handleEditAfp = (afp) => {
    setAfpModalMode('edit');
    setCurrentAfp(afp);
    setAfpFormData({
      nombre: afp.nombre,
      comision_fija: afp.comision_fija.toString(),
      comision_variable: afp.comision_variable.toString(),
      seguro: afp.seguro.toString(),
      aporte: afp.aporte.toString()
    });
    setAfpFormErrors({});
    setShowAfpModal(true);
  };

  const handleCloseAfpModal = () => {
    setShowAfpModal(false);
    setCurrentAfp(null);
    setAfpFormData({
      nombre: '',
      comision_fija: '',
      comision_variable: '',
      seguro: '',
      aporte: ''
    });
    setAfpFormErrors({});
  };

  const handleAfpInputChange = (e) => {
    const { name, value } = e.target;
    setAfpFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (afpFormErrors[name]) {
      setAfpFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateAfpForm = () => {
    const errors = {};
    
    // Validar nombre
    if (!afpFormData.nombre.trim()) {
      errors.nombre = 'El nombre de la AFP es obligatorio';
    } else if (afpFormData.nombre.length > 100) {
      errors.nombre = 'El nombre no puede exceder 100 caracteres';
    }
    
    // Validar comisión fija
    const comisionFija = parseFloat(afpFormData.comision_fija);
    if (!afpFormData.comision_fija.trim()) {
      errors.comision_fija = 'La comisión fija es obligatoria';
    } else if (isNaN(comisionFija) || comisionFija < 0 || comisionFija > 100) {
      errors.comision_fija = 'La comisión fija debe ser un número entre 0 y 100';
    }
    
    // Validar comisión variable
    const comisionVariable = parseFloat(afpFormData.comision_variable);
    if (!afpFormData.comision_variable.trim()) {
      errors.comision_variable = 'La comisión variable es obligatoria';
    } else if (isNaN(comisionVariable) || comisionVariable < 0 || comisionVariable > 100) {
      errors.comision_variable = 'La comisión variable debe ser un número entre 0 y 100';
    }
    
    // Validar seguro
    const seguro = parseFloat(afpFormData.seguro);
    if (!afpFormData.seguro.trim()) {
      errors.seguro = 'El seguro es obligatorio';
    } else if (isNaN(seguro) || seguro < 0 || seguro > 100) {
      errors.seguro = 'El seguro debe ser un número entre 0 y 100';
    }
    
    // Validar aporte
    const aporte = parseFloat(afpFormData.aporte);
    if (!afpFormData.aporte.trim()) {
      errors.aporte = 'El aporte es obligatorio';
    } else if (isNaN(aporte) || aporte < 0 || aporte > 100) {
      errors.aporte = 'El aporte debe ser un número entre 0 y 100';
    }
    
    setAfpFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitAfp = async (e) => {
    e.preventDefault();
    
    if (!validateAfpForm()) {
      return;
    }
    
    setSavingAfp(true);
    
    try {
      const afpDataToSend = {
        nombre: afpFormData.nombre.trim(),
        comision_fija: parseFloat(afpFormData.comision_fija),
        comision_variable: parseFloat(afpFormData.comision_variable),
        seguro: parseFloat(afpFormData.seguro),
        aporte: parseFloat(afpFormData.aporte)
      };
      
      let response;
      if (afpModalMode === 'create') {
        response = await afpService.crearAfp(afpDataToSend);
      } else {
        response = await afpService.actualizarAfp(currentAfp.id, afpDataToSend);
      }
      
      if (response.success) {
        console.log('✅ AFP guardada exitosamente:', response.data);
        
        // Recargar datos
        await fetchAfpData();
        
        // Cerrar modal
        handleCloseAfpModal();
        
        // Mostrar mensaje de éxito
        mostrarMensaje('success', response.message);
      } else {
        throw new Error(response.message || 'Error al guardar AFP');
      }
    } catch (error) {
      console.error('❌ Error al guardar AFP:', error);
      if (error.response?.data?.errors) {
        setAfpFormErrors(error.response.data.errors);
      } else {
        mostrarMensaje('error', error.message);
      }
    } finally {
      setSavingAfp(false);
    }
  };

  const handleDeleteAfp = async (afp) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar la AFP "${afp.nombre}"?`)) {
      return;
    }
    
    try {
      const response = await afpService.eliminarAfp(afp.id);
      
      if (response.success) {
        console.log('✅ AFP eliminada exitosamente');
        
        // Recargar datos
        await fetchAfpData();
        
        // Mostrar mensaje de éxito
        mostrarMensaje('success', response.message);
      } else {
        throw new Error(response.message || 'Error al eliminar AFP');
      }
    } catch (error) {
      console.error('❌ Error al eliminar AFP:', error);
      mostrarMensaje('error', error.message);
    }
  };

  // Función para filtrar datos PLAME
  const filtrarPlameData = () => {
    if (!filtroCodigo && !filtroNombre) {
      return plameData;
    }
    
    return plameData.filter(codigo => {
      const coincideCodigo = !filtroCodigo || 
        codigo.codigo.toLowerCase().includes(filtroCodigo.toLowerCase());
      const coincideNombre = !filtroNombre || 
        codigo.descripcion.toLowerCase().includes(filtroNombre.toLowerCase());
      
      return coincideCodigo && coincideNombre;
    });
  };

  // Función para limpiar filtros PLAME
  const limpiarFiltrosPlame = () => {
    setFiltroCodigo('');
    setFiltroNombre('');
  };

  // Funciones para manejar Tramos de Renta
  const handleCreateTramoRenta = () => {
    setTramoRentaModalMode('create');
    setCurrentTramoRenta(null);
    setTramoRentaFormData({
      tramo: '',
      desde: '',
      hasta: '',
      tasa: ''
    });
    setTramoRentaFormErrors({});
    setShowTramoRentaModal(true);
  };

  const handleEditTramoRenta = (tramo) => {
    setTramoRentaModalMode('edit');
    setCurrentTramoRenta(tramo);
    setTramoRentaFormData({
      tramo: tramo.tramo.toString(),
      desde: tramo.desde.toString(),
      hasta: tramo.hasta ? tramo.hasta.toString() : '',
      tasa: tramo.tasa.toString()
    });
    setTramoRentaFormErrors({});
    setShowTramoRentaModal(true);
  };

  const handleCloseTramoRentaModal = () => {
    setShowTramoRentaModal(false);
    setCurrentTramoRenta(null);
    setTramoRentaFormData({
      tramo: '',
      desde: '',
      hasta: '',
      tasa: ''
    });
    setTramoRentaFormErrors({});
  };

  const handleTramoRentaInputChange = (e) => {
    const { name, value } = e.target;
    setTramoRentaFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (tramoRentaFormErrors[name]) {
      setTramoRentaFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateTramoRentaForm = () => {
    const errors = {};
    
    // Validar tramo
    const tramo = parseInt(tramoRentaFormData.tramo);
    if (!tramoRentaFormData.tramo.trim()) {
      errors.tramo = 'El número de tramo es obligatorio';
    } else if (isNaN(tramo) || tramo < 1 || tramo > 10) {
      errors.tramo = 'El tramo debe ser un número entre 1 y 10';
    }
    
    // Validar desde
    const desde = parseFloat(tramoRentaFormData.desde);
    if (!tramoRentaFormData.desde.trim()) {
      errors.desde = 'El valor "desde" es obligatorio';
    } else if (isNaN(desde) || desde < 0 || desde > 999.99) {
      errors.desde = 'El valor "desde" debe ser un número entre 0 y 999.99';
    }
    
    // Validar hasta (opcional)
    if (tramoRentaFormData.hasta.trim()) {
      const hasta = parseFloat(tramoRentaFormData.hasta);
      if (isNaN(hasta) || hasta < 0 || hasta > 999.99) {
        errors.hasta = 'El valor "hasta" debe ser un número entre 0 y 999.99';
      } else if (hasta <= desde) {
        errors.hasta = 'El valor "hasta" debe ser mayor que "desde"';
      }
    }
    
    // Validar tasa
    const tasa = parseFloat(tramoRentaFormData.tasa);
    if (!tramoRentaFormData.tasa.trim()) {
      errors.tasa = 'La tasa es obligatoria';
    } else if (isNaN(tasa) || tasa < 0 || tasa > 100) {
      errors.tasa = 'La tasa debe ser un número entre 0 y 100';
    }
    
    setTramoRentaFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitTramoRenta = async (e) => {
    e.preventDefault();
    
    if (!validateTramoRentaForm()) {
      return;
    }
    
    setSavingTramoRenta(true);
    
    try {
      const tramoDataToSend = {
        tramo: parseInt(tramoRentaFormData.tramo),
        desde: parseFloat(tramoRentaFormData.desde),
        hasta: tramoRentaFormData.hasta.trim() ? parseFloat(tramoRentaFormData.hasta) : null,
        tasa: parseFloat(tramoRentaFormData.tasa)
      };
      
      let response;
      if (tramoRentaModalMode === 'create') {
        response = await tramoRentaService.crearTramoRenta(tramoDataToSend);
      } else {
        response = await tramoRentaService.actualizarTramoRenta(currentTramoRenta.id, tramoDataToSend);
      }
      
      if (response.success) {
        console.log('✅ Tramo de Renta guardado exitosamente:', response.data);
        
        // Recargar datos
        await fetchTramosRentaData();
        
        // Cerrar modal
        handleCloseTramoRentaModal();
        
        // Mostrar mensaje de éxito
        mostrarMensaje('success', response.message);
      } else {
        throw new Error(response.message || 'Error al guardar Tramo de Renta');
      }
    } catch (error) {
      console.error('❌ Error al guardar Tramo de Renta:', error);
      if (error.response?.data?.errors) {
        setTramoRentaFormErrors(error.response.data.errors);
      } else {
        mostrarMensaje('error', error.message);
      }
    } finally {
      setSavingTramoRenta(false);
    }
  };

  const handleDeleteTramoRenta = async (tramo) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el Tramo ${tramo.tramo}?`)) {
      return;
    }
    
    try {
      const response = await tramoRentaService.eliminarTramoRenta(tramo.id);
      
      if (response.success) {
        console.log('✅ Tramo de Renta eliminado exitosamente');
        
        // Recargar datos
        await fetchTramosRentaData();
        
        // Mostrar mensaje de éxito
        mostrarMensaje('success', response.message);
      } else {
        throw new Error(response.message || 'Error al eliminar Tramo de Renta');
      }
    } catch (error) {
      console.error('❌ Error al eliminar Tramo de Renta:', error);
      mostrarMensaje('error', error.message);
    }
  };

  // Funciones para manejar Fórmulas
  const handleCreateFormula = () => {
    setFormulaModalMode('create');
    setCurrentFormula(null);
    setFormulaFormData({
      codigo_plame: '',
      regimen_laboral_id: '',
      formula: '',
      searchPlame: '',
      showPlameList: false
    });
    setFormulaFormErrors({});
    
    // Cargar datos auxiliares si no están disponibles
    if (!Array.isArray(codigosPlameFormulas) || codigosPlameFormulas.length === 0) {
      fetchCodigosPlameFormulas();
    }
    if (!Array.isArray(regimenesLaboralesFormulas) || regimenesLaboralesFormulas.length === 0) {
      fetchRegimenesLaboralesFormulas();
    }
    
    setShowFormulaModal(true);
  };

  const handleEditFormula = (formula) => {
    console.log('🔄 Editando fórmula:', formula);
    console.log('📋 codigosPlameFormulas disponibles:', codigosPlameFormulas.length);
    console.log('👥 regimenesLaboralesFormulas disponibles:', regimenesLaboralesFormulas.length);
    
    setFormulaModalMode('edit');
    setCurrentFormula(formula);
    
    // Buscar la descripción del código PLAME en los datos cargados
    const codigoPlameInfo = codigosPlameFormulas.find(c => c.codigo_plame === formula.codigo);
    const searchText = codigoPlameInfo 
      ? `${formula.codigo} - ${codigoPlameInfo.descripcion}`
      : formula.codigo || '';
    
    console.log('🔍 Código PLAME encontrado:', codigoPlameInfo);
    console.log('🎯 searchText:', searchText);
    console.log('🏷️ regimen_laboral_id:', formula.id_tipo || formula.regimen_laboral_id);
    
    setFormulaFormData({
      codigo_plame: formula.codigo || '',
      regimen_laboral_id: formula.id_tipo || formula.regimen_laboral_id || '',
      formula: formula.formula || '',
      searchPlame: searchText,
      showPlameList: false
    });
    setFormulaFormErrors({});
    
    // Cargar datos auxiliares si no están disponibles
    if (!Array.isArray(codigosPlameFormulas) || codigosPlameFormulas.length === 0) {
      console.log('🔄 Cargando códigos PLAME...');
      fetchCodigosPlameFormulas();
    }
    if (!Array.isArray(regimenesLaboralesFormulas) || regimenesLaboralesFormulas.length === 0) {
      console.log('🔄 Cargando regímenes laborales...');
      fetchRegimenesLaboralesFormulas();
    }
    
    setShowFormulaModal(true);
  };

  const handleCloseFormulaModal = () => {
    setShowFormulaModal(false);
    setCurrentFormula(null);
    setFormulaFormData({
      codigo_plame: '',
      regimen_laboral_id: '',
      formula: '',
      searchPlame: '',
      showPlameList: false
    });
    setFormulaFormErrors({});
  };

  const handleFormulaInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormulaFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (formulaFormErrors[name]) {
      setFormulaFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateFormulaForm = () => {
    const errors = {};
    
    console.log('🔍 Validando formulario con datos:', formulaFormData);
    
    if (!formulaFormData.codigo_plame || !formulaFormData.codigo_plame.trim()) {
      errors.codigo_plame = 'El código PLAME es requerido';
    }
    
    if (!formulaFormData.regimen_laboral_id) {
      errors.regimen_laboral_id = 'El régimen laboral es requerido';
    }
    
    if (!formulaFormData.formula || !formulaFormData.formula.trim()) {
      errors.formula = 'La fórmula es requerida';
    }
    
    console.log('🔍 Errores de validación:', errors);
    console.log('🔍 Formulario es válido:', Object.keys(errors).length === 0);
    
    setFormulaFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitFormula = async (e) => {
    e.preventDefault();
    
    if (!validateFormulaForm()) {
      return;
    }
    
    // Mapear los datos al formato que espera el backend
    const dataToSend = {
      codigo: formulaFormData.codigo_plame,
      id_tipo: formulaFormData.regimen_laboral_id,
      formula: formulaFormData.formula
    };
    
    console.log('📤 Datos originales del form:', formulaFormData);
    console.log('📤 Datos mapeados para enviar:', dataToSend);
    console.log('🔧 Modo:', formulaModalMode);
    
    try {
      let response;
      if (formulaModalMode === 'create') {
        console.log('➕ Creando nueva fórmula...');
        response = await formulaService.crearFormula(dataToSend);
      } else {
        console.log('✏️ Actualizando fórmula:', currentFormula.id);
        response = await formulaService.actualizarFormula(currentFormula.id, dataToSend);
      }
      
      console.log('✅ Respuesta del servidor:', response);
      
      if (response.success) {
        mostrarMensaje('success', response.message);
        handleCloseFormulaModal();
        fetchFormulasData(); // Recargar datos
      }
    } catch (error) {
      console.error('❌ Error completo:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error response data:', error.response?.data);
      
      const mensaje = error.response?.data?.message || error.message || `Error al ${formulaModalMode === 'create' ? 'crear' : 'actualizar'} la fórmula`;
      mostrarMensaje('error', mensaje);
    }
  };

  const handleDeleteFormula = async (formula) => {
    if (window.confirm(`¿Estás seguro de que deseas eliminar la fórmula "${formula.nombre}"?`)) {
      try {
        const response = await formulaService.eliminarFormula(formula.id);
        if (response.success) {
          mostrarMensaje('success', response.message);
          fetchFormulasData(); // Recargar datos
        }
      } catch (error) {
        const mensaje = error.response?.data?.message || 'Error al eliminar la fórmula';
        mostrarMensaje('error', mensaje);
      }
    }
  };

  const tabs = [
    { id: 'sueldo-minimo', name: 'Información General', icon: '💰' },
    { id: 'afp', name: 'AFP Perú', icon: '🏦' },
    { id: 'plame', name: 'Códigos PLAME', icon: '📋' },
    { id: 'tramos-renta', name: 'Tramos de Renta', icon: '📊' },
    { id: 'regimenes-laborales', name: 'Regímenes Laborales', icon: '👥' },
    { id: 'formulas', name: 'Fórmulas', icon: '🧮' }
  ];

  const renderSueldoMinimo = () => (
    <div className="space-y-8">
      {/* Sección Sueldo Mínimo */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Sueldo Mínimo Vital</h3>
            <p className="text-sm text-gray-600">Configura el monto actual del sueldo mínimo vital</p>
          </div>
          <button 
            onClick={() => sueldoMinimo.length > 0 && abrirModalEdicion('sueldo_minimo', sueldoMinimo[0].monto)}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span>Editar Monto</span>
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Concepto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sueldoMinimo.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">💰</span>
                      <div>
                        <div className="font-medium">Sueldo Mínimo Vital</div>
                        <div className="text-xs text-gray-500">Remuneración mínima mensual</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="text-lg font-bold text-teal-600">S/ {item.monto.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      item.vigente ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {item.vigente ? 'Vigente' : 'Inactivo'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sección UIT */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">UIT - Unidad Impositiva Tributaria</h3>
            <p className="text-sm text-gray-600">Configura el valor actual de la UIT para cálculos tributarios</p>
          </div>
          <button 
            onClick={() => uitData.length > 0 && abrirModalEdicion('uit', uitData[0].monto)}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span>Editar Valor</span>
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Concepto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {uitData.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">📊</span>
                      <div>
                        <div className="font-medium">Unidad Impositiva Tributaria</div>
                        <div className="text-xs text-gray-500">Base para cálculos tributarios</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="text-lg font-bold text-orange-600">S/ {item.monto.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      item.vigente ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {item.vigente ? 'Vigente' : 'Inactivo'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Información adicional */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Información General</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Sueldo Mínimo:</strong> Remuneración mínima mensual establecida por ley</li>
                <li><strong>UIT:</strong> Unidad de medida tributaria actualizada anualmente por SUNAT</li>
                <li><strong>Uso UIT:</strong> Base para multas, infracciones, deducciones y límites tributarios</li>
                <li><strong>Actualización:</strong> Ambos valores se actualizan mediante normas legales</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAFP = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
                 <div>
           <h3 className="text-lg font-semibold text-gray-900">Gestión de AFP</h3>
           <p className="text-sm text-gray-600">Configura las comisiones fijas, variables y aportes de las AFP en Perú</p>
           <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-500">
             <div className="flex items-center space-x-1">
               <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
               <span>Comisión Fija: Sobre remuneración</span>
             </div>
             <div className="flex items-center space-x-1">
               <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
               <span>Comisión Variable: Sobre rentabilidad</span>
             </div>
             <div className="flex items-center space-x-1">
               <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
               <span>Seguro: Invalidez y sobrevivencia</span>
             </div>
             <div className="flex items-center space-x-1">
               <div className="w-2 h-2 bg-green-500 rounded-full"></div>
               <span>Aporte: Obligatorio del trabajador</span>
             </div>
           </div>
         </div>
        <button 
          onClick={handleCreateAfp}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Nueva AFP</span>
        </button>
      </div>

      {/* Error de carga de AFP */}
      {afpError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-700 text-sm">
              Error al cargar AFP: {afpError}
            </p>
          </div>
          <button
            onClick={fetchAfpData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Tabla de AFP */}
             <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loadingAfp ? (
          <div className="p-8 text-center">
            <div className="flex items-center justify-center space-x-2">
              <svg className="animate-spin w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-gray-600">Cargando AFP...</span>
            </div>
          </div>
        ) : afpData.length === 0 ? (
          <div className="p-8 text-center">
            <div className="flex flex-col items-center">
              <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No hay AFP registradas
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Aún no se han registrado AFP en el sistema. 
                {isAdmin && ' Como administrador, puedes registrar la primera AFP.'}
              </p>
              {isAdmin && (
                <button
                  onClick={handleCreateAfp}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Registrar Primera AFP</span>
                </button>
              )}
            </div>
          </div>
        ) : (
         <table className="min-w-full divide-y divide-gray-200">
           <thead className="bg-gray-50">
             <tr>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AFP</th>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                 <div className="flex flex-col">
                   <span>Comisión Fija</span>
                   <span className="text-gray-400 font-normal">%</span>
                 </div>
               </th>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                 <div className="flex flex-col">
                   <span>Comisión Variable</span>
                   <span className="text-gray-400 font-normal">%</span>
                 </div>
               </th>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                 <div className="flex flex-col">
                   <span>Seguro</span>
                   <span className="text-gray-400 font-normal">%</span>
                 </div>
               </th>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                 <div className="flex flex-col">
                   <span>Aporte</span>
                   <span className="text-gray-400 font-normal">%</span>
                 </div>
               </th>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
             </tr>
           </thead>
           <tbody className="bg-white divide-y divide-gray-200">
             {afpData.map((afp) => (
               <tr key={afp.id} className="hover:bg-gray-50">
                 <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{afp.nombre}</td>
                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="font-medium text-blue-600">{afp.comision_fija}%</span>
                 </td>
                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="font-medium text-purple-600">{afp.comision_variable}%</span>
                 </td>
                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                   <span className="font-medium text-orange-600">{afp.seguro}%</span>
                 </td>
                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                   <span className="font-medium text-green-600">{afp.aporte}%</span>
                 </td>
                 <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      Activa
                   </span>
                 </td>
                 <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button 
                      onClick={() => handleEditAfp(afp)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Editar
                    </button>
                    <button 
                      onClick={() => handleDeleteAfp(afp)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Eliminar
                    </button>
                 </td>
               </tr>
             ))}
           </tbody>
         </table>
        )}
       </div>
    </div>
  );

  const renderRegimenesLaborales = () => <RegimenesLaborales />;

  const renderTramosRenta = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Gestión de Tramos de Renta - Quinta Categoría</h3>
          <p className="text-sm text-gray-600">Configura los tramos del impuesto a la renta de quinta categoría</p>
          <div className="mt-2 text-xs text-gray-500">
            <p>Los tramos se expresan en UIT (Unidad Impositiva Tributaria). Los rangos determinan las tasas progresivas del impuesto.</p>
          </div>
        </div>
        <button 
          onClick={handleCreateTramoRenta}
          className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Nuevo Tramo</span>
        </button>
      </div>

      {/* Error de carga de Tramos de Renta */}
      {tramosRentaError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-700 text-sm">
              Error al cargar Tramos de Renta: {tramosRentaError}
            </p>
                     </div>
          <button
            onClick={fetchTramosRentaData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Reintentar
          </button>
      </div>
      )}

      {/* Tabla de Tramos de Renta */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loadingTramosRenta ? (
          <div className="p-8 text-center">
            <div className="flex items-center justify-center space-x-2">
              <svg className="animate-spin w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
              <span className="text-gray-600">Cargando Tramos de Renta...</span>
          </div>
            </div>
        ) : tramosRentaData.length === 0 ? (
          <div className="p-8 text-center">
            <div className="flex flex-col items-center">
              <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No hay Tramos de Renta registrados
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Aún no se han registrado tramos de renta en el sistema. 
                {isAdmin && ' Como administrador, puedes registrar el primer tramo.'}
              </p>
              {isAdmin && (
                <button
                  onClick={handleCreateTramoRenta}
                  className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200"
                >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
                  <span>Registrar Primer Tramo</span>
        </button>
              )}
      </div>
          </div>
        ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tramo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Desde (UIT)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hasta (UIT)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tasa (%)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rango</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
              {tramosRentaData.map((tramo) => (
              <tr key={tramo.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Tramo {tramo.tramo}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {tramo.desde} UIT
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {tramo.hasta ? `${tramo.hasta} UIT` : 'En adelante'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="font-semibold text-orange-600">{tramo.tasa}%</span>
                </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {tramo.hasta ? `De ${tramo.desde} a ${tramo.hasta} UIT` : `Más de ${tramo.desde} UIT`}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button 
                      onClick={() => handleEditTramoRenta(tramo)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Editar
                    </button>
                    <button 
                      onClick={() => handleDeleteTramoRenta(tramo)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Eliminar
                    </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {/* Información adicional */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Información sobre los Tramos de Renta</h3>
            <div className="mt-2 text-sm text-blue-700">
                             <ul className="list-disc list-inside space-y-1">
                 <li>Los tramos se calculan sobre la renta neta anual de quinta categoría</li>
                 <li>Se aplica el sistema de tasas progresivas acumulativas</li>
                 <li>La UIT (Unidad Impositiva Tributaria) se actualiza anualmente</li>
                 <li>Cada tramo aplica su tasa solo al exceso del tramo anterior</li>
               </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFormulas = () => {
    console.log('🎨 renderFormulas - formulasData:', formulasData);
    console.log('🎨 renderFormulas - Es array?:', Array.isArray(formulasData));
    console.log('🎨 renderFormulas - Length:', formulasData?.length);
    
    return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Gestión de Fórmulas</h3>
          <p className="text-sm text-gray-600">Configura las fórmulas de cálculo para conceptos de planilla</p>
          <div className="mt-2 text-xs text-gray-500">
            <p>Define las fórmulas matemáticas para el cálculo automático de conceptos</p>
            <p>Total de fórmulas: {Array.isArray(formulasData) ? formulasData.length : 0}</p>
          </div>
        </div>
        <button 
          onClick={handleCreateFormula}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Nueva Fórmula</span>
        </button>
      </div>

      {/* Error de carga de Fórmulas */}
      {formulasError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-700 text-sm">
              Error al cargar fórmulas: {formulasError}
            </p>
          </div>
          <button
            onClick={fetchFormulasData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Reintentar
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loadingFormulas ? (
          <div className="p-8 text-center">
            <div className="flex items-center justify-center space-x-2">
              <svg className="animate-spin w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-gray-600">Cargando fórmulas...</span>
            </div>
          </div>
        ) : !Array.isArray(formulasData) || formulasData.length === 0 ? (
          <div className="p-8 text-center">
            <div className="flex flex-col items-center">
              <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No hay fórmulas registradas
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Aún no se han registrado fórmulas en el sistema. 
                {isAdmin && ' Como administrador, puedes registrar la primera fórmula.'}
              </p>
            </div>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción PLAME</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Régimen Laboral</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fórmula</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(formulasData) && formulasData.map((formula) => (
                <tr key={formula.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formula.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-bold text-blue-600">
                    {formula.codigo}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate">
                    {formula.plame_configuracion?.descripcion || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formula.regimen_laboral?.nombre || `ID: ${formula.id_tipo}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded text-green-700 font-medium">
                      {formula.formula}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button 
                      onClick={() => handleEditFormula(formula)}
                      className="text-blue-600 hover:text-blue-900 transition-colors"
                    >
                      Editar
                    </button>
                    <button 
                      onClick={() => handleDeleteFormula(formula)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Información adicional */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">Variables de Fórmulas</h3>
            <div className="mt-2 text-sm text-green-700">
              <ul className="list-disc list-inside space-y-1">
                <li><strong>R:</strong> Remuneración básica del trabajador</li>
                <li><strong>M:</strong> Sueldo mínimo vital vigente</li>
                <li><strong>A:</strong> Porcentaje de AFP (varía según la AFP del trabajador)</li>
                <li><strong>Operadores:</strong> +, -, *, /, (), % para porcentajes</li>
                <li><strong>Ejemplo:</strong> R*A = Remuneración por porcentaje de AFP</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderPLAME = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Tabla 22: "INGRESOS, TRIBUTOS Y DESCUENTOS"</h3>
          <p className="text-sm text-gray-600">Códigos oficiales PLAME - Planilla Mensual de Pagos</p>
          <div className="mt-2 text-xs text-gray-500">
            <p>Total de configuraciones: {plameData.length} | Mostrando: {filtrarPlameData().length}</p>
          </div>
        </div>
        <p className="text-xs text-gray-500">Haz clic en un código para editar</p>
      </div>

      {/* Filtros */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700">Filtros de búsqueda</h4>
          {(filtroCodigo || filtroNombre) && (
            <button
              onClick={limpiarFiltrosPlame}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              Limpiar filtros
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Filtro por código */}
          <div>
            <label htmlFor="filtro-codigo" className="block text-xs font-medium text-gray-600 mb-1">
              Buscar por código
            </label>
            <div className="relative">
              <input
                type="text"
                id="filtro-codigo"
                value={filtroCodigo}
                onChange={(e) => setFiltroCodigo(e.target.value)}
                placeholder="Ej: 0601, 0607..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pl-9"
              />
              <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          {/* Filtro por nombre/descripción */}
          <div>
            <label htmlFor="filtro-nombre" className="block text-xs font-medium text-gray-600 mb-1">
              Buscar por descripción
            </label>
            <div className="relative">
              <input
                type="text"
                id="filtro-nombre"
                value={filtroNombre}
                onChange={(e) => setFiltroNombre(e.target.value)}
                placeholder="Ej: essalud, aporte, comisión..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pl-9"
              />
              <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Indicador de resultados */}
        {(filtroCodigo || filtroNombre) && (
          <div className="mt-3 text-xs text-gray-600">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {filtrarPlameData().length} resultados encontrados
            </span>
          </div>
        )}
      </div>

      {/* Error de carga */}
      {plameError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-700 text-sm">
              Error al cargar configuraciones PLAME: {plameError}
            </p>
          </div>
          <button
            onClick={fetchPlameData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Reintentar
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loadingPlame ? (
          <div className="p-8 text-center">
            <div className="flex items-center justify-center space-x-2">
              <svg className="animate-spin w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-gray-600">Cargando configuraciones PLAME...</span>
            </div>
          </div>
        ) : plameData.length === 0 ? (
          <div className="p-8 text-center">
            <div className="flex flex-col items-center">
              <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No hay configuraciones PLAME
              </h3>
              <p className="text-gray-600 text-sm">
                No se encontraron configuraciones PLAME en el sistema.
              </p>
            </div>
          </div>
        ) : filtrarPlameData().length === 0 ? (
          <div className="p-8 text-center">
            <div className="flex flex-col items-center">
              <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No se encontraron resultados
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                No hay configuraciones PLAME que coincidan con los filtros aplicados.
              </p>
              <button
                onClick={limpiarFiltrosPlame}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                Limpiar filtros
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th rowSpan="3" className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase border-r border-gray-300">
                    CÓDIGO
                  </th>
                  <th rowSpan="3" className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase border-r border-gray-300">
                    DESCRIPCIÓN DE LOS INGRESOS, TRIBUTOS Y DESCUENTOS
                  </th>
                  <th colSpan="7" className="px-2 py-1 text-center text-xs font-bold text-blue-700 uppercase border-r border-gray-300 bg-blue-50">
                    EMPLEADOR
                  </th>
                  <th colSpan="5" className="px-2 py-1 text-center text-xs font-bold text-green-700 uppercase border-r border-gray-300 bg-green-50">
                    TRABAJADOR
                  </th>
                  <th colSpan="2" className="px-2 py-1 text-center text-xs font-bold text-purple-700 uppercase bg-purple-50">
                    PENSIONISTA
                  </th>
                </tr>
                <tr>
                  <th className="px-1 py-1 text-center text-xs font-medium text-blue-600 border-r border-gray-200 bg-blue-50">
                    ESSALUD SEGURO REGULAR TRABAJADOR
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-blue-600 border-r border-gray-200 bg-blue-50">
                    ESSALUD - CBSSP - SEG TRAB PESQUERO
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-blue-600 border-r border-gray-200 bg-blue-50">
                    ESSALUD SEGURO AGRARIO / ACUICULTOR
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-blue-600 border-r border-gray-200 bg-blue-50">
                    ESSALUD SCTR
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-blue-600 border-r border-gray-200 bg-blue-50">
                    IMPUESTO EXTRAORD. DE SOLIDARIDAD (8)
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-blue-600 border-r border-gray-200 bg-blue-50">
                    FONDO DERECHOS SOCIALES DEL ARTISTA
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-blue-600 border-r border-gray-300 bg-blue-50">
                    SENATI
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-green-600 border-r border-gray-200 bg-green-50">
                    SISTEMA NACIONAL DE PENSIONES 19990
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-green-600 border-r border-gray-200 bg-green-50">
                    SISTEMA PRIVADO DE PENSIONES
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-green-600 border-r border-gray-200 bg-green-50">
                    FONDO COMPL. JUBIL MET Y SIDER.H
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-green-600 border-r border-gray-200 bg-green-50">
                    REG.ESP PENSIONES TRAB. PESQUERO
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-green-600 border-r border-gray-300 bg-green-50">
                    RENTA 5TA CATEGORÍA RETENCIONES
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-purple-600 border-r border-gray-200 bg-purple-50">
                    ESSALUD SEGURO REGULAR PENSIONISTA
                  </th>
                  <th className="px-1 py-1 text-center text-xs font-medium text-purple-600 bg-purple-50">
                    CONTRIB. SOLIDARIA ASISTENCIA PREVIS.
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtrarPlameData().map((codigo) => (
                  <tr key={codigo.id} className="hover:bg-gray-50">
                    <td className="px-2 py-2 text-center border-r border-gray-300">
                      <button
                        type="button"
                        onClick={() => setPlameEditRow({ ...codigo })}
                        className="text-sm font-mono font-bold text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-purple-500 rounded"
                        title="Editar configuración"
                      >
                        {codigo.codigo}
                      </button>
                    </td>
                    <td className="px-2 py-2 text-xs text-gray-900 border-r border-gray-300 max-w-xs">
                      {codigo.descripcion}
                    </td>
                    {/* EMPLEADOR */}
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-200 bg-blue-25">
                      <span className={codigo.empleador.essalud_regular === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.empleador.essalud_regular}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-200 bg-blue-25">
                      <span className={codigo.empleador.essalud_cbssp_seg === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.empleador.essalud_cbssp_seg}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-200 bg-blue-25">
                      <span className={codigo.empleador.essalud_agrario === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.empleador.essalud_agrario}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-200 bg-blue-25">
                      <span className={codigo.empleador.essalud_sctr === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.empleador.essalud_sctr}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-200 bg-blue-25">
                      <span className={codigo.empleador.impuesto_extraord_solidaridad === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.empleador.impuesto_extraord_solidaridad}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-200 bg-blue-25">
                      <span className={codigo.empleador.fondo_derechos_artista === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.empleador.fondo_derechos_artista}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-300 bg-blue-25">
                      <span className={codigo.empleador.senati === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.empleador.senati}
                      </span>
                    </td>
                    {/* TRABAJADOR */}
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-200 bg-green-25">
                      <span className={codigo.trabajador.sistema_nacional_19990 === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.trabajador.sistema_nacional_19990}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-200 bg-green-25">
                      <span className={codigo.trabajador.sistema_privado_pensiones === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.trabajador.sistema_privado_pensiones}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-200 bg-green-25">
                      <span className={codigo.trabajador.fondo_compl_jubil === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.trabajador.fondo_compl_jubil}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-200 bg-green-25">
                      <span className={codigo.trabajador.reg_esp_pensiones_pesquero === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.trabajador.reg_esp_pensiones_pesquero}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-300 bg-green-25">
                      <span className={codigo.trabajador.renta_5ta_categoria === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.trabajador.renta_5ta_categoria}
                      </span>
                    </td>
                    {/* PENSIONISTA */}
                    <td className="px-1 py-2 text-center text-xs font-semibold border-r border-gray-200 bg-purple-25">
                      <span className={codigo.pensionista.essalud_regular_pensionista === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.pensionista.essalud_regular_pensionista}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-center text-xs font-semibold bg-purple-25">
                      <span className={codigo.pensionista.contrib_solidaria_asistencia === 'SI' ? 'text-green-600' : 'text-red-600'}>
                        {codigo.pensionista.contrib_solidaria_asistencia}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal editar código PLAME */}
      {plameEditRow && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => !savingPlameEdit && setPlameEditRow(null)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Editar código PLAME: {plameEditRow.codigo}</h3>
                <button type="button" onClick={() => !savingPlameEdit && setPlameEditRow(null)} className="text-gray-400 hover:text-gray-600 p-1 rounded focus:outline-none focus:ring-2 focus:ring-purple-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-6 space-y-6">
                {plameEditError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{plameEditError}</div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Descripción</label>
                  <input
                    type="text"
                    value={plameEditRow.descripcion || ''}
                    onChange={(e) => updatePlameEditField('root', 'descripcion', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Descripción del concepto"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <h4 className="text-xs font-bold text-blue-800 uppercase mb-3">Empleador</h4>
                    <div className="space-y-2">
                      {[
                        ['essalud_regular', 'ESSALUD Regular Trabajador'],
                        ['essalud_cbssp_seg', 'ESSALUD CBSSP Seg. Pesquero'],
                        ['essalud_agrario', 'ESSALUD Agrario/Acuicultor'],
                        ['essalud_sctr', 'ESSALUD SCTR'],
                        ['impuesto_extraord_solidaridad', 'Impuesto Extraord. Solidaridad'],
                        ['fondo_derechos_artista', 'Fondo Derechos Artista'],
                        ['senati', 'SENATI']
                      ].map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-700 truncate flex-1">{label}</span>
                          <select value={plameEditRow.empleador?.[key] || 'NO'} onChange={(e) => updatePlameEditField('empleador', key, e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1 w-16 focus:outline-none focus:ring-1 focus:ring-purple-500">
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                    <h4 className="text-xs font-bold text-green-800 uppercase mb-3">Trabajador</h4>
                    <div className="space-y-2">
                      {[
                        ['sistema_nacional_19990', 'Sist. Nacional Pensiones 19990'],
                        ['sistema_privado_pensiones', 'Sist. Privado Pensiones'],
                        ['fondo_compl_jubil', 'Fondo Compl. Jubil. Met.'],
                        ['reg_esp_pensiones_pesquero', 'Reg. Esp. Pensiones Pesquero'],
                        ['renta_5ta_categoria', 'Renta 5ta Categoría']
                      ].map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-700 truncate flex-1">{label}</span>
                          <select value={plameEditRow.trabajador?.[key] || 'NO'} onChange={(e) => updatePlameEditField('trabajador', key, e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1 w-16 focus:outline-none focus:ring-1 focus:ring-purple-500">
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                    <h4 className="text-xs font-bold text-purple-800 uppercase mb-3">Pensionista</h4>
                    <div className="space-y-2">
                      {[
                        ['essalud_regular_pensionista', 'ESSALUD Regular Pensionista'],
                        ['contrib_solidaria_asistencia', 'Contrib. Solidaria Asistencia']
                      ].map(([key, label]) => (
                        <div key={key} className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-700 truncate flex-1">{label}</span>
                          <select value={plameEditRow.pensionista?.[key] || 'NO'} onChange={(e) => updatePlameEditField('pensionista', key, e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1 w-16 focus:outline-none focus:ring-1 focus:ring-purple-500">
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-2">
                <button type="button" onClick={() => setPlameEditRow(null)} disabled={savingPlameEdit} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50">Cancelar</button>
                <button type="button" onClick={() => savePlameEdit(plameEditRow)} disabled={savingPlameEdit} className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50">
                  {savingPlameEdit ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Información adicional */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-purple-800">Información sobre Códigos PLAME</h3>
            <div className="mt-2 text-sm text-purple-700">
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Códigos PLAME:</strong> Planilla Mensual de Trabajadores - Tabla oficial SUNAT</li>
                <li><strong>Empleador:</strong> Contribuciones y aportes que debe realizar el empleador</li>
                <li><strong>Trabajador:</strong> Descuentos que se aplican al trabajador</li>
                <li><strong>Pensionista:</strong> Aportes específicos para pensionistas</li>
                <li><strong>SI/NO:</strong> Indica si el concepto está afecto a la contribución correspondiente</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Volver</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Configuración del Sistema</h1>
                <p className="text-gray-600">Gestiona las tablas generales y parámetros del sistema</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">{user?.name?.charAt(0)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {activeTab === 'sueldo-minimo' && renderSueldoMinimo()}
          {activeTab === 'afp' && renderAFP()}
          {activeTab === 'plame' && renderPLAME()}
          {activeTab === 'tramos-renta' && renderTramosRenta()}
          {activeTab === 'regimenes-laborales' && renderRegimenesLaborales()}
        {activeTab === 'formulas' && renderFormulas()}
        </div>
      </div>

      {/* Modal de Edición */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {editModal.type === 'sueldo_minimo' ? 'Editar Sueldo Mínimo Vital' : 'Editar UIT'}
              </h3>
              <button
                onClick={cerrarModalEdicion}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nuevo Monto (S/.)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
              
              <div className="flex space-x-3 justify-end">
                <button
                  onClick={cerrarModalEdicion}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  onClick={guardarCambio}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Crear/Editar AFP */}
      {showAfpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header del Modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {afpModalMode === 'create' ? 'Nueva AFP' : 'Editar AFP'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {afpModalMode === 'create' ? 'Registra una nueva AFP en el sistema' : 'Modifica los datos de la AFP'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseAfpModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={savingAfp}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenido del Modal */}
            <form onSubmit={handleSubmitAfp} className="p-6">
              {/* Campo Nombre */}
              <div className="mb-4">
                <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de la AFP <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={afpFormData.nombre}
                  onChange={handleAfpInputChange}
                  maxLength="100"
                  placeholder="AFP Integra"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    afpFormErrors.nombre ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={savingAfp}
                />
                {afpFormErrors.nombre && (
                  <p className="mt-1 text-sm text-red-600">{afpFormErrors.nombre}</p>
                )}
              </div>

              {/* Campo Comisión Fija */}
              <div className="mb-4">
                <label htmlFor="comision_fija" className="block text-sm font-medium text-gray-700 mb-2">
                  Comisión Fija (%) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="comision_fija"
                  name="comision_fija"
                  value={afpFormData.comision_fija}
                  onChange={handleAfpInputChange}
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="1.55"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    afpFormErrors.comision_fija ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={savingAfp}
                />
                {afpFormErrors.comision_fija && (
                  <p className="mt-1 text-sm text-red-600">{afpFormErrors.comision_fija}</p>
                )}
              </div>

              {/* Campo Comisión Variable */}
              <div className="mb-4">
                <label htmlFor="comision_variable" className="block text-sm font-medium text-gray-700 mb-2">
                  Comisión Variable (%) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="comision_variable"
                  name="comision_variable"
                  value={afpFormData.comision_variable}
                  onChange={handleAfpInputChange}
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="1.25"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    afpFormErrors.comision_variable ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={savingAfp}
                />
                {afpFormErrors.comision_variable && (
                  <p className="mt-1 text-sm text-red-600">{afpFormErrors.comision_variable}</p>
                )}
              </div>

              {/* Campo Seguro */}
              <div className="mb-4">
                <label htmlFor="seguro" className="block text-sm font-medium text-gray-700 mb-2">
                  Seguro (%) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="seguro"
                  name="seguro"
                  value={afpFormData.seguro}
                  onChange={handleAfpInputChange}
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="1.36"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    afpFormErrors.seguro ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={savingAfp}
                />
                {afpFormErrors.seguro && (
                  <p className="mt-1 text-sm text-red-600">{afpFormErrors.seguro}</p>
                )}
              </div>

              {/* Campo Aporte */}
              <div className="mb-6">
                <label htmlFor="aporte" className="block text-sm font-medium text-gray-700 mb-2">
                  Aporte (%) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="aporte"
                  name="aporte"
                  value={afpFormData.aporte}
                  onChange={handleAfpInputChange}
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="10.00"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    afpFormErrors.aporte ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={savingAfp}
                />
                {afpFormErrors.aporte && (
                  <p className="mt-1 text-sm text-red-600">{afpFormErrors.aporte}</p>
                )}
              </div>

              {/* Botones */}
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseAfpModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                  disabled={savingAfp}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingAfp}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {savingAfp ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Guardando...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{afpModalMode === 'create' ? 'Crear AFP' : 'Actualizar AFP'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para Crear/Editar Tramo de Renta */}
      {showTramoRentaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header del Modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {tramoRentaModalMode === 'create' ? 'Nuevo Tramo de Renta' : 'Editar Tramo de Renta'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {tramoRentaModalMode === 'create' ? 'Registra un nuevo tramo de renta en el sistema' : 'Modifica los datos del tramo de renta'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseTramoRentaModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={savingTramoRenta}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenido del Modal */}
            <form onSubmit={handleSubmitTramoRenta} className="p-6">
              {/* Campo Tramo */}
              <div className="mb-4">
                <label htmlFor="tramo" className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Tramo <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="tramo"
                  name="tramo"
                  value={tramoRentaFormData.tramo}
                  onChange={handleTramoRentaInputChange}
                  min="1"
                  max="10"
                  placeholder="1"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    tramoRentaFormErrors.tramo ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={savingTramoRenta}
                />
                {tramoRentaFormErrors.tramo && (
                  <p className="mt-1 text-sm text-red-600">{tramoRentaFormErrors.tramo}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">Número del tramo (1-10)</p>
              </div>

              {/* Campo Desde */}
              <div className="mb-4">
                <label htmlFor="desde" className="block text-sm font-medium text-gray-700 mb-2">
                  Desde (UIT) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="desde"
                  name="desde"
                  value={tramoRentaFormData.desde}
                  onChange={handleTramoRentaInputChange}
                  step="0.01"
                  min="0"
                  max="999.99"
                  placeholder="0.00"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    tramoRentaFormErrors.desde ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={savingTramoRenta}
                />
                {tramoRentaFormErrors.desde && (
                  <p className="mt-1 text-sm text-red-600">{tramoRentaFormErrors.desde}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">Valor inicial del rango en UIT</p>
              </div>

              {/* Campo Hasta */}
              <div className="mb-4">
                <label htmlFor="hasta" className="block text-sm font-medium text-gray-700 mb-2">
                  Hasta (UIT) <span className="text-gray-400">(Opcional)</span>
                </label>
                <input
                  type="number"
                  id="hasta"
                  name="hasta"
                  value={tramoRentaFormData.hasta}
                  onChange={handleTramoRentaInputChange}
                  step="0.01"
                  min="0"
                  max="999.99"
                  placeholder="5.00"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    tramoRentaFormErrors.hasta ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={savingTramoRenta}
                />
                {tramoRentaFormErrors.hasta && (
                  <p className="mt-1 text-sm text-red-600">{tramoRentaFormErrors.hasta}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">Valor final del rango en UIT. Dejar vacío para "en adelante"</p>
              </div>

              {/* Campo Tasa */}
              <div className="mb-6">
                <label htmlFor="tasa" className="block text-sm font-medium text-gray-700 mb-2">
                  Tasa (%) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="tasa"
                  name="tasa"
                  value={tramoRentaFormData.tasa}
                  onChange={handleTramoRentaInputChange}
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="8.00"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    tramoRentaFormErrors.tasa ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={savingTramoRenta}
                />
                {tramoRentaFormErrors.tasa && (
                  <p className="mt-1 text-sm text-red-600">{tramoRentaFormErrors.tasa}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">Porcentaje de impuesto para este tramo</p>
              </div>

              {/* Botones */}
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseTramoRentaModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                  disabled={savingTramoRenta}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingTramoRenta}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {savingTramoRenta ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Guardando...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{tramoRentaModalMode === 'create' ? 'Crear Tramo' : 'Actualizar Tramo'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para Crear/Editar Fórmula */}
      {showFormulaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header del Modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {formulaModalMode === 'create' ? 'Nueva Fórmula' : 'Editar Fórmula'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {formulaModalMode === 'create' ? 'Registra una nueva fórmula de cálculo' : 'Modifica los datos de la fórmula'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseFormulaModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenido del Modal */}
            <form onSubmit={handleSubmitFormula} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Campo Código PLAME con buscador */}
                <div>
                  <label htmlFor="codigo_plame_search" className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar Código PLAME <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="codigo_plame_search"
                      placeholder="Buscar por código o descripción..."
                      value={formulaFormData.searchPlame || ''}
                      onChange={(e) => {
                        setFormulaFormData(prev => ({
                          ...prev,
                          searchPlame: e.target.value,
                          showPlameList: true
                        }));
                      }}
                      onFocus={() => {
                        setFormulaFormData(prev => ({
                          ...prev,
                          showPlameList: true
                        }));
                      }}
                      onBlur={() => {
                        // Delay para permitir que el clic en la lista funcione
                        setTimeout(() => {
                          setFormulaFormData(prev => ({
                            ...prev,
                            showPlameList: false
                          }));
                        }, 200);
                      }}
                      className="w-full px-3 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <svg className="w-4 h-4 text-gray-400 absolute left-3 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  
                  {/* Lista filtrada de códigos PLAME - Mostrar siempre que haya texto o esté enfocado */}
                  {(formulaFormData.searchPlame || formulaFormData.showPlameList) && (
                    <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg bg-white shadow-lg">
                      {Array.isArray(codigosPlameFormulas) && 
                        codigosPlameFormulas
                          .filter(codigo => 
                            !formulaFormData.searchPlame || 
                            codigo.codigo_plame.toLowerCase().includes(formulaFormData.searchPlame.toLowerCase()) ||
                            codigo.descripcion.toLowerCase().includes(formulaFormData.searchPlame.toLowerCase())
                          )
                          .slice(0, 10) // Mostrar solo los primeros 10 resultados
                          .map((codigo) => (
                            <div
                              key={codigo.id}
                              onClick={() => {
                                setFormulaFormData(prev => ({
                                  ...prev,
                                  codigo_plame: codigo.codigo_plame,
                                  searchPlame: `${codigo.codigo_plame} - ${codigo.descripcion}`,
                                  showPlameList: false
                                }));
                              }}
                              className={`p-2 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                                formulaFormData.codigo_plame === codigo.codigo_plame ? 'bg-green-50 border-green-200' : ''
                              }`}
                            >
                              <div className="text-sm font-medium text-gray-900">{codigo.codigo_plame}</div>
                              <div className="text-xs text-gray-600 truncate">{codigo.descripcion}</div>
                            </div>
                          ))
                      }
                      {Array.isArray(codigosPlameFormulas) && 
                        formulaFormData.searchPlame && 
                        codigosPlameFormulas.filter(codigo => 
                          codigo.codigo_plame.toLowerCase().includes(formulaFormData.searchPlame.toLowerCase()) ||
                          codigo.descripcion.toLowerCase().includes(formulaFormData.searchPlame.toLowerCase())
                        ).length === 0 && (
                          <div className="p-3 text-sm text-gray-500 text-center">
                            No se encontraron códigos PLAME
                          </div>
                        )
                      }
                    </div>
                  )}
                  
                  {formulaFormErrors.codigo_plame && (
                    <p className="mt-1 text-sm text-red-600">{formulaFormErrors.codigo_plame}</p>
                  )}
                </div>

                {/* Campo Régimen Laboral */}
                <div>
                  <label htmlFor="regimen_laboral_id" className="block text-sm font-medium text-gray-700 mb-2">
                    Régimen Laboral <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="regimen_laboral_id"
                    name="regimen_laboral_id"
                    value={formulaFormData.regimen_laboral_id}
                    onChange={handleFormulaInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      formulaFormErrors.regimen_laboral_id ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Seleccionar régimen laboral</option>
                    {Array.isArray(regimenesLaboralesFormulas) && regimenesLaboralesFormulas.map((regimen) => (
                      <option key={regimen.id} value={regimen.id}>
                        {regimen.nombre}
                      </option>
                    ))}
                  </select>
                  {formulaFormErrors.regimen_laboral_id && (
                    <p className="mt-1 text-sm text-red-600">{formulaFormErrors.regimen_laboral_id}</p>
                  )}
                </div>
              </div>

              {/* Campo Fórmula */}
              <div className="mb-6">
                <label htmlFor="formula" className="block text-sm font-medium text-gray-700 mb-2">
                  Fórmula Matemática <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="formula"
                  name="formula"
                  value={formulaFormData.formula}
                  onChange={handleFormulaInputChange}
                  placeholder="Ej: R * 0.10 (donde R = Remuneración básica)"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono ${
                    formulaFormErrors.formula ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {formulaFormErrors.formula && (
                  <p className="mt-1 text-sm text-red-600">{formulaFormErrors.formula}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Variables disponibles: R (Remuneración), M (Sueldo mínimo), A (% AFP)
                </p>
              </div>

              {/* Botones */}
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseFormulaModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{formulaModalMode === 'create' ? 'Crear Fórmula' : 'Actualizar Fórmula'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mensajes */}
      {mensaje.texto && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`p-4 rounded-lg shadow-lg ${
            mensaje.tipo === 'success' 
              ? 'bg-green-100 border border-green-400 text-green-700' 
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            {mensaje.texto}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySelector; 